/**
 * Profile sweep along a plan polyline (TRIM round, freeform spike Phase 1 —
 * docs/bim-plan/13 §3): the general "2D profile along a path" primitive this
 * codebase never had. extrudeProfile.ts extrudes a profile across ONE straight
 * frame; this sweeps a profile along an N-segment (optionally closed) path with
 * WATERTIGHT MITERED CORNERS — the per-vertex miter proven by the roof gutter
 * (categories/roof/trim.ts, Matt 2026-08-11): every profile point at outward
 * offset w slides along its segment to X(w) = C + w·(n1+n2)/(1+n1·n2), and since
 * (n1+n2)/(1+n1·n2) = n1 + ud1·k1 = n2 − ud2·k2, both segments sample the SAME
 * world point — the joint closes for any profile, arcs included.
 *
 * Profile convention (kernel trimProfiles.ts): a closed (o, z) ring — o projects
 * toward the LEFT normal of travel, z is vertical above `zBase`. Curved PATH
 * segments arrive pre-flattened (flattenPath — the walls convention); their facet
 * joints are near-collinear corners whose miter coefficients are tiny, so a curved
 * run sweeps smoothly with no special casing.
 *
 * PER-STATION Z (arced-wall-terminations WARP FIX, 2026-08-20 — Matt's report):
 * `opts.zOfS` gives a z offset as a function of arc length s along the path, and
 * `opts.zWeights` says how much each profile vertex follows it (1 = ride the arc,
 * 0 = stay flat — a wall band's arch side vs its flat prism junction). When zOfS
 * is set, every segment SUBDIVIDES into stations (`stationStepFt`) and strips pair
 * MATCHING stations — a ruled ladder. This exists because the first arced-band
 * build earcut the varying-z footprint ring, and earcut's fan triangulation (fine
 * for the roof prism's PLANAR surfaces) connected far-apart stations of a
 * NON-planar surface into visibly twisted sheets. Triangulation choice matters
 * once z varies along the run; the ladder is the correct one by construction.
 *
 * Output is flat-shaded, duplicated verts, winding-derived normals — the railing/
 * roof-trim accumulator conventions. Feature EDGES emit longitudinally at SHARP
 * profile vertices only (interior turn > featureAngleDeg) — in zOfS mode only at
 * weighted (arc-following) vertices, so the flat junction to a host prism stays
 * seam-free; open path ends outline their cap (skipped in zOfS mode for the same
 * seam reason).
 */
import earcut from 'earcut'
import type { Pt2 } from '@aj/bim-kernel'

export interface SweepResult {
  positions: Float32Array
  normals: Float32Array
  indices: Uint32Array
  edges: number[]
  zMin: number
  zMax: number
}

const EPS = 1e-9

const signedArea = (ring: Pt2[]): number => {
  let s = 0
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    s += ring[j][0] * ring[i][1] - ring[i][0] * ring[j][1]
  }
  return s / 2
}

export function sweepProfileAlong(
  profile: Pt2[],
  pathPts: Pt2[],
  closed: boolean,
  zBase: number,
  opts?: {
    miterLimit?: number
    featureAngleDeg?: number
    /** WARP FIX: per-station z offset by arc length along the path */
    zOfS?: (s: number) => number
    /** per INPUT-profile-vertex weight on zOfS (default 0 = flat) */
    zWeights?: number[]
    /** station subdivision length when zOfS is set (default 0.5 ft) */
    stationStepFt?: number
    /**
     * CANTED WALLS round: per-end cap control for PIECEWISE sweeps — a piece whose
     * end is a mid-run split (an opening jamb station) suppresses its own cap when
     * a neighboring full-height piece already provides that face (coincident caps
     * z-fight on the visible jamb reveal). Defaults: both true on open paths.
     */
    caps?: { start?: boolean; end?: boolean }
  },
): SweepResult | null {
  if (profile.length < 3 || pathPts.length < 2) return null
  const miterLimit = opts?.miterLimit ?? 6
  const featureCos = Math.cos(((opts?.featureAngleDeg ?? 30) * Math.PI) / 180)
  const zOfS = opts?.zOfS

  // pair profile points with their zOfS weights so winding normalization and
  // dedupe carry both together
  let pairs = profile.map((p, i) => ({ p, w: opts?.zWeights?.[i] ?? 0 }))
  if (signedArea(profile) <= 0) pairs = [...pairs].reverse()
  pairs = pairs.filter((q, i) => {
    const nx = pairs[(i + 1) % pairs.length]
    return Math.hypot(nx.p[0] - q.p[0], nx.p[1] - q.p[1]) > EPS
  })
  if (pairs.length < 3) return null
  const prof: Pt2[] = pairs.map((q) => q.p)
  const wts: number[] = pairs.map((q) => q.w)

  // sharp profile vertices (longitudinal feature-edge sites): interior turn between
  // the incoming and outgoing profile edges exceeds the feature angle
  const sharp: boolean[] = prof.map((p, i) => {
    const a = prof[(i - 1 + prof.length) % prof.length]
    const b = prof[(i + 1) % prof.length]
    const u: Pt2 = [p[0] - a[0], p[1] - a[1]]
    const v: Pt2 = [b[0] - p[0], b[1] - p[1]]
    const lu = Math.hypot(u[0], u[1]), lv = Math.hypot(v[0], v[1])
    if (lu < EPS || lv < EPS) return false
    return (u[0] * v[0] + u[1] * v[1]) / (lu * lv) < featureCos
  })

  // clean path: drop degenerate segments (and the closing duplicate, if passed)
  const pts: Pt2[] = []
  for (const p of pathPts) {
    const last = pts[pts.length - 1]
    if (!last || Math.hypot(p[0] - last[0], p[1] - last[1]) > EPS) pts.push(p)
  }
  if (closed && pts.length > 1) {
    const a = pts[0], b = pts[pts.length - 1]
    if (Math.hypot(a[0] - b[0], a[1] - b[1]) < EPS) pts.pop()
  }
  const nSeg = closed ? pts.length : pts.length - 1
  if (nSeg < 1 || pts.length < 2) return null

  const segLen = (i: number): number => {
    const a = pts[i], b = pts[(i + 1) % pts.length]
    return Math.hypot(b[0] - a[0], b[1] - a[1])
  }
  const segDir = (i: number): Pt2 => {
    const a = pts[i], b = pts[(i + 1) % pts.length]
    const l = segLen(i)
    return [(b[0] - a[0]) / l, (b[1] - a[1]) / l]
  }
  const leftN = (d: Pt2): Pt2 => [-d[1], d[0]]

  // per-segment-end miter coefficient (0 = square). The slide is along +ud at the
  // segment's END corner and applies with the same formula at its START corner —
  // symmetry of X(w) guarantees both segments meet exactly (see header).
  const miterK = (segIdx: number, atEnd: boolean): number => {
    const nbrIdx = atEnd ? segIdx + 1 : segIdx - 1
    if (!closed && (nbrIdx < 0 || nbrIdx >= nSeg)) return 0
    const wrapped = ((nbrIdx % nSeg) + nSeg) % nSeg
    const ud = segDir(segIdx)
    const n1 = leftN(ud)
    const n2 = leftN(segDir(wrapped))
    const denom = 1 + (n1[0] * n2[0] + n1[1] * n2[1])
    if (Math.abs(denom) < 0.05) return 0            // reflex-degenerate: square cut
    const k = ((n1[0] + n2[0]) * ud[0] + (n1[1] + n2[1]) * ud[1]) / denom
    return Math.abs(k) > miterLimit ? 0 : k
  }

  const pos: number[] = [], nrm: number[] = [], idx: number[] = []
  let vc = 0
  const pushTri = (a: number[], b: number[], c: number[]) => {
    const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2]
    const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2]
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
    const len = Math.hypot(nx, ny, nz)
    if (len < 1e-12) return           // degenerate (a tapered arc end) — skip
    nx /= len; ny /= len; nz /= len
    pos.push(...a, ...b, ...c)
    nrm.push(nx, ny, nz, nx, ny, nz, nx, ny, nz)
    idx.push(vc, vc + 1, vc + 2)
    vc += 3
  }

  const edges: number[] = []
  let zMin = Infinity, zMax = -Infinity
  const stationStep = Math.max(opts?.stationStepFt ?? 0.5, 0.05)

  let segStartS = 0
  for (let s = 0; s < nSeg; s++) {
    const A = pts[s]
    const ud = segDir(s)
    const n = leftN(ud)
    const len = segLen(s)
    const kA = miterK(s, false), kB = miterK(s, true)
    // world position of profile point q (weight w) at arc length sAbs, anchored at
    // the station point `st`, with kAlong the miter slide (segment ends only)
    const posAt = (st: Pt2, kAlong: number, q: Pt2, w: number, sAbs: number): number[] => {
      const z = zBase + q[1] + (zOfS && w ? w * zOfS(sAbs) : 0)
      if (z < zMin) zMin = z
      if (z > zMax) zMax = z
      return [st[0] + (n[0] + ud[0] * kAlong) * q[0], st[1] + (n[1] + ud[1] * kAlong) * q[0], z]
    }
    // WARP FIX: with zOfS, subdivide the segment into stations and pair MATCHING
    // stations (the ruled ladder — see header); without it, one span per segment
    const nSt = zOfS ? Math.max(1, Math.ceil(len / stationStep)) : 1
    for (let j = 0; j < nSt; j++) {
      const t0 = j / nSt, t1 = (j + 1) / nSt
      const P0: Pt2 = [A[0] + ud[0] * len * t0, A[1] + ud[1] * len * t0]
      const P1: Pt2 = [A[0] + ud[0] * len * t1, A[1] + ud[1] * len * t1]
      const k0 = j === 0 ? kA : 0
      const k1 = j === nSt - 1 ? kB : 0
      const s0 = segStartS + len * t0
      const s1 = segStartS + len * t1
      // lateral strips around the profile (two tris per quad — a mitered strip is
      // slightly ruled, not planar); outward = right of CCW travel in (o, z) — the
      // gutter's orientation test, verbatim
      for (let i = 0; i < prof.length; i++) {
        const jv = (i + 1) % prof.length
        const [oi, zi] = prof[i], [oj, zj] = prof[jv]
        const doo = oj - oi, dz = zj - zi
        const ol = Math.hypot(doo, dz) || 1
        const out3 = [n[0] * (dz / ol), n[1] * (dz / ol), -doo / ol]
        const Pa = posAt(P0, k0, prof[i], wts[i], s0), Pb = posAt(P1, k1, prof[i], wts[i], s1)
        const Qa = posAt(P0, k0, prof[jv], wts[jv], s0), Qb = posAt(P1, k1, prof[jv], wts[jv], s1)
        const cx = (Pb[1] - Pa[1]) * (Qa[2] - Pa[2]) - (Pb[2] - Pa[2]) * (Qa[1] - Pa[1])
        const cy = (Pb[2] - Pa[2]) * (Qa[0] - Pa[0]) - (Pb[0] - Pa[0]) * (Qa[2] - Pa[2])
        const cz = (Pb[0] - Pa[0]) * (Qa[1] - Pa[1]) - (Pb[1] - Pa[1]) * (Qa[0] - Pa[0])
        if (cx * out3[0] + cy * out3[1] + cz * out3[2] > 0) {
          pushTri(Pa, Pb, Qa); pushTri(Qa, Pb, Qb)
        } else {
          pushTri(Pa, Qa, Pb); pushTri(Qa, Qb, Pb)
        }
        // longitudinal feature edge at a SHARP profile vertex (mitered endpoints,
        // so adjacent spans' lines meet); in zOfS mode only at ARC-following
        // vertices — the flat junction to a host prism must stay seam-free
        if (sharp[i] && (!zOfS || wts[i] > 0)) {
          edges.push(Pa[0], Pa[1], Pa[2], Pb[0], Pb[1], Pb[2])
        }
      }
    }
    // end caps at the OPEN ends only (interior/closed corners seal by miter).
    // In zOfS mode the effective end profile includes the weighted offsets; a
    // profile collapsed by the arc taper caps nothing (pushTri drops degenerates).
    const capAt = (end: Pt2, sign: number, sAbs: number) => {
      const eff: Pt2[] = prof.map((q, i): Pt2 => [q[0], q[1] + (zOfS && wts[i] ? wts[i] * zOfS(sAbs) : 0)])
      if (Math.abs(signedArea(eff)) < 1e-6) return
      const capTris = earcut(eff.flatMap((p) => [p[0], p[1]]))
      const at = (m: number): number[] => posAt(end, 0, prof[m], wts[m], sAbs)
      for (let t = 0; t < capTris.length; t += 3) {
        const P = at(capTris[t]), Q = at(capTris[t + 1]), R = at(capTris[t + 2])
        const nx = (Q[1] - P[1]) * (R[2] - P[2]) - (Q[2] - P[2]) * (R[1] - P[1])
        const ny = (Q[2] - P[2]) * (R[0] - P[0]) - (Q[0] - P[0]) * (R[2] - P[2])
        if (nx * ud[0] * sign + ny * ud[1] * sign > 0) pushTri(P, Q, R)
        else pushTri(P, R, Q)
      }
      // cap outline — the crisp end read (skipped in zOfS mode: a band's end face
      // is coplanar with its host's own end face, and the outline would draw a
      // seam across it)
      if (!zOfS) {
        for (let i = 0; i < prof.length; i++) {
          const a = at(i), b = at((i + 1) % prof.length)
          edges.push(a[0], a[1], a[2], b[0], b[1], b[2])
        }
      }
    }
    if (!closed && s === 0 && (opts?.caps?.start ?? true)) capAt(A, -1, segStartS)
    if (!closed && s === nSeg - 1 && (opts?.caps?.end ?? true)) {
      capAt([A[0] + ud[0] * len, A[1] + ud[1] * len], 1, segStartS + len)
    }
    segStartS += len
  }

  if (!vc) return null
  return {
    positions: new Float32Array(pos),
    normals: new Float32Array(nrm),
    indices: new Uint32Array(idx),
    edges, zMin, zMax,
  }
}
